<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import HeaderView from '../components/HeaderView.vue';
import FooterView from '../components/FooterView.vue';
import DemoSection from '../components/DemoSection.vue';
import CMMSShowcaseSection from '../components/CMMSShowcaseSection.vue';
import StickyMobileSection from '../components/StickyMobileSection.vue';
import AccessAnywhereSection from '../components/AccessAnywhereSection.vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const mockupsContainerRef = ref<HTMLElement | null>(null);
const leftMockupRef = ref<HTMLElement | null>(null);
const rightMockupRef = ref<HTMLElement | null>(null);
const centerMockupRef = ref<HTMLElement | null>(null);
const accentTextRef = ref<HTMLElement | null>(null);

onMounted(() => {
  // Animación del título gradiente pasando de negro a azul
  gsap.to(accentTextRef.value, {
    backgroundPosition: "0% center",
    duration: 1,
    delay: 0.6, // Espera un poco a que termine la intro de fadeInUp
    ease: "power2.inOut"
  });

  // GSAP MatchMedia for responsive animations
  const mm = gsap.matchMedia();

  mm.add("(min-width: 768px)", () => {
    // ScrollTrigger timeline that scrubs based on scroll position
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: mockupsContainerRef.value,
        start: "top 45%", // Empieza cuando el top de los iphones entra al viewport
        end: "top 5%",   // Termina cuando están cerca del tope de la pantalla
        scrub: 1,         // Añade 1seg de inercia/suavizado a la animación atada al scroll
      }
    });

    // El iPhone izquierdo sale hacia la izquierda y rota -20 grados
    tl.to(leftMockupRef.value, {
      xPercent: -80,
      yPercent: 5,
      rotation: -20,
      ease: "power2.out"
    }, 0);

    // El iPhone derecho sale hacia la derecha y rota 20 grados
    tl.to(rightMockupRef.value, {
      xPercent: 80,
      yPercent: 5,
      rotation: 20,
      ease: "power2.out"
    }, 0);

    // El iPhone central puede subir un poquito para más dinamismo
    tl.to(centerMockupRef.value, {
      yPercent: -6,
      ease: "power2.out"
    }, 0);
  });

  onUnmounted(() => {
    mm.revert();
    ScrollTrigger.getAll().forEach(t => t.kill());
  });
});
</script>

<template>
  <div class="landing-page">
    <HeaderView />

    <main class="main-content">
      <section class="hero-section">

        <!-- Badge Superior -->
        <div class="hero-badge animate-fade-in-up">
          <span class="hero-badge-label">Nuevo</span>
          <span class="hero-badge-text">
            Software CMMS en la nube con dispositivos inteligentes y geolocalización
            <svg class="hero-badge-icon" fill="currentColor" viewBox="0 0 256 256">
              <path
                d="M216,120a8,8,0,0,1-8,8,64.07,64.07,0,0,0-64,64,8,8,0,0,1-16,0,64.07,64.07,0,0,0-64-64,8,8,0,0,1,0-16,64.07,64.07,0,0,0,64-64,8,8,0,0,1,16,0,64.07,64.07,0,0,0,64,64A8,8,0,0,1,216,120Z">
              </path>
            </svg>
            <span class="hero-badge-arrow">›</span>
          </span>
        </div>

        <!-- Titulo H1 -->
        <h1 class="hero-title animate-fade-in-up">
          Software <span class="text-accent text-accent-animated" ref="accentTextRef">CMMS/GMAO</span><br />
          <span class="text-dark">para empresas</span>
        </h1>

        <!-- Subtitulo -->
        <p class="hero-subtitle animate-fade-in-up">
          Una plataforma profesional para gestionar mantenimiento industrial y activos
          <br> empresariales.
        </p>

        <!-- Bloque GSAP Scroll Animado de Celulares -->
        <div class="mockups-container animate-fade-in-up" ref="mockupsContainerRef">

          <div class="hero-mockup-wrapper mockup-left" ref="leftMockupRef">
            <img src="../assets/mockups/iphoneleft.png" alt="Módulos de mantenimiento" class="mockup-image-left" />
          </div>

          <div class="hero-mockup-wrapper mockup-right" ref="rightMockupRef">
            <img src="../assets/mockups/iphoneright.png" alt="Reportes detallados" class="mockup-image-right" />
          </div>

          <div class="hero-mockup-wrapper mockup-center" ref="centerMockupRef">
            <img src="../assets/mockups/iphonemiddle.png" alt="Dashboard de Operario" class="mockup-image-center" />
          </div>
        </div>

      </section>
      <div class="cta-container animate-fade-in-up">
        <button class="btn-cta group">
          Solicita una Demo
          <svg class="btn-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
          </svg>
        </button>
      </div>

      <!-- Sección CMMS Showcase (Simple White Layout) -->
      <!-- Sección CMMS Showcase (Simple White Layout) -->
      <CMMSShowcaseSection />

      <!-- New Sticky Mobile Section -->
      <StickyMobileSection />

      <AccessAnywhereSection />

      <DemoSection />
    </main>

    <FooterView />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

/* Global Layout */
.landing-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  font-family: 'Manrope', sans-serif;
  width: 100%;
}

.main-content {
  flex: 1;
  width: 100%;
  overflow-x: clip;
  display: flex;
  flex-direction: column;
}

/* Hero Section */
.hero-section {
  width: 100%;
  position: relative;
  padding: 90px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 1400px;
  margin: 0 auto;
}

/* Utilities */
.text-accent {
  color: #129BEF;
  font-weight: 700;
}

.text-accent-animated {
  background: linear-gradient(90deg, #129BEF 0%, #129BEF 45%, #173854 65%, #000000 100%);
  background-size: 300% auto;
  background-position: 100% center; /* Inicia en el sector negro */
  color: transparent !important;
  -webkit-background-clip: text;
  background-clip: text;
}

.text-dark {
  color: #111827;
  font-weight: 700;
}

.text-gray {
  color: #6B7280;
}

.text-amber {
  color: #F59E0B;
}

.font-bold {
  font-weight: 700;
}

.font-normal {
  font-weight: 400;
}

/* Badge Superior */
.hero-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  margin-bottom: 20px;
}

@media (min-width: 768px) {
  .hero-badge {
    flex-direction: row;
  }
}

.hero-badge-label {
  background-color: #129BEF;
  color: #ffffff;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 9999px;
}

.hero-badge-text {
  font-size: 18px;
  color: #4B5563;
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 4px;
}

.hero-badge-icon {
  width: 16px;
  height: 16px;
  color: #F59E0B;
}

.hero-badge-arrow {
  color: #129BEF;
  font-weight: 700;
  margin-left: 4px;
  cursor: pointer;
}

.hero-badge-arrow:hover {
  text-decoration: underline;
}

/* Titles */
.hero-title {
  font-size: 40px;
  line-height: 1.1;
  letter-spacing: -0.04em;
  color: #111827;
  font-weight: 800;
  margin: 0 auto 32px auto;
  max-width: 1000px;
}

@media (min-width: 768px) {
  .hero-title {
    font-size: 72px;
  }
}

.hero-subtitle {
  font-size: 20px;
  color: #6B7280;
  line-height: 1.6;
  font-weight: 500;
  margin: 0 auto 60px auto;
}

@media (min-width: 768px) {
  .hero-subtitle {
    font-size: 20px;
  }
}

/* Mockups Layout */
.mockups-container {
  position: relative;
  width: 100%;
  max-width: 1000px;
  height: 1000px;
  margin: 0 auto;
}

/* @media (min-width: 768px) {
  .mockups-container {
    height: 550px;
  }
} */

@media (min-width: 1024px) {
  .mockups-container {
    height: 750px;
  }
}

/* Phones centering mechanics */
.hero-mockup-wrapper {
  position: absolute;
  top: 0;
  transform-origin: center bottom;
}

.mockup-image-left {
  width: 400px;
  display: block;
}

.mockup-image-right {
  width: 400px;
  display: block;
}

.mockup-image-center {
  width: 900px;
  display: block;
}

/* Center Phone: Anchored perfectly to the horizontal center */
.mockup-center {
  z-index: 10;
  left: 50%;
  margin-left: -450px;
  /* offset exactly by half of 900px */
}

/* Left & Right Phones: Anchored perfectly to the same center, hidden behind the middle phone on desktop */
.mockup-left {
  display: none;
  z-index: 5;
  left: 70%;
  margin-left: -200px;
  /* offset exactly by half of 400px */
}

.mockup-right {
  display: none;
  z-index: 5;
  left: 30%;
  margin-left: -200px;
  /* offset exactly by half of 400px */
}

@media (min-width: 768px) {
  .mockup-left {
    display: block;
  }

  .mockup-right {
    display: block;
  }
}

/* Floating Badges */
.floating-badge {
  display: none;
  /* Blocked on mobile */
  position: absolute;
  z-index: 20;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 16px;
  border: 1px solid rgba(229, 231, 235, 0.6);
  align-items: center;
  gap: 16px;
}

@media (min-width: 1024px) {
  .floating-badge {
    display: flex;
  }
}

.badge-left {
  left: -2%;
  top: 55%;
}

.badge-right-top {
  right: -2%;
  top: 35%;
}

.badge-right-bottom {
  right: -5%;
  top: 70%;
}

.badge-text-container {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.badge-text {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.25;
}

.badge-accent-green {
  color: #059669;
}

.badge-icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-green {
  background-color: #ecfdf5;
  border: 1px solid #d1fae5;
  color: #059669;
}

.icon-amber {
  background-color: #fffbeb;
  border: 1px solid #fef3c7;
  color: #f59e0b;
}

.icon-blue {
  background-color: rgba(18, 155, 239, 0.1);
  border: 1px solid rgba(18, 155, 239, 0.2);
  color: #129BEF;
}

.icon-svg {
  width: 20px;
  height: 20px;
}

/* CTA */
.cta-container {
  position: relative;
  z-index: 30;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: #ffffff;
  padding-top: 50px;
  height: 200px;
  box-shadow: 0 -35px 40px 20px rgb(255 255 255);

  /* Difumina oscureciendo solo hacia arriba (spread negativo para que no salga por los lados ni por abajo) */
  border-top: 2px solid;
  border-image: linear-gradient(to right, #ffffff, #e2e8f0, #ffffff) 1;
}

.btn-cta {
  background-color: #129BEF;
  color: #ffffff;
  font-weight: 700;
  font-size: 18px;
  padding: 16px 36px;
  border-radius: 9999px;

  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: inherit;
}

.btn-cta:hover {
  background-color: #0c82ce;
  transform: translateY(-4px);
}

.btn-arrow {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.btn-cta:hover .btn-arrow {
  transform: translateX(4px);
}

/* Animations Core */
.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Initial delays for intro */
.hero-badge.animate-fade-in-up {
  animation-delay: 0.1s;
  animation-duration: 0.6s;
}

.hero-title.animate-fade-in-up {
  animation-delay: 0.2s;
}

.hero-subtitle.animate-fade-in-up {
  animation-delay: 0.3s;
}

.mockups-container.animate-fade-in-up {
  animation-delay: 0.4s;
  animation-duration: 1s;
}

.cta-container.animate-fade-in-up {
  animation-delay: 0.7s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
}

@keyframes floatDelayed {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes floatSlow {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

.animate-float {
  animation: float 5s ease-in-out infinite;
}

.animate-float-delayed {
  animation: floatDelayed 6s ease-in-out infinite;
  animation-delay: 2s;
}

.animate-float-slow {
  animation: floatSlow 7s ease-in-out infinite;
  animation-delay: 1s;
}

.cta-container {
  height: 150px;
  padding-top: 30px;
}
</style>
